import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { 
  Smartphone, 
  Globe, 
  Workflow, 
  Brain,
  ArrowRight,
  CheckCircle2,
  Terminal,
  Star,
  Loader2,
  ShoppingCart
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ProductsPage = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [orderDialog, setOrderDialog] = useState({ open: false, product: null });
  const [orderDescription, setOrderDescription] = useState("");
  const [ordering, setOrdering] = useState(false);

  const categories = [
    { value: "all", label: "Tümü" },
    { value: "mobile_app", label: "Mobil Uygulama", icon: Smartphone },
    { value: "web_app", label: "Web Uygulama", icon: Globe },
    { value: "automation", label: "Otomasyon", icon: Workflow },
    { value: "ai_solution", label: "Yapay Zeka", icon: Brain }
  ];

  const getCategoryIcon = (category) => {
    const icons = {
      mobile_app: Smartphone,
      web_app: Globe,
      automation: Workflow,
      ai_solution: Brain
    };
    return icons[category] || Globe;
  };

  const getCategoryColor = (category) => {
    const colors = {
      mobile_app: "#00d4aa",
      web_app: "#0ea5e9",
      automation: "#a855f7",
      ai_solution: "#f97316"
    };
    return colors[category] || "#00d4aa";
  };

  const getCategoryLabel = (category) => {
    const labels = {
      mobile_app: "Mobil Uygulama",
      web_app: "Web Uygulama",
      automation: "Otomasyon",
      ai_solution: "Yapay Zeka"
    };
    return labels[category] || category;
  };

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API}/products?active_only=true`);
      setProducts(response.data);
    } catch (error) {
      console.error("Error fetching products:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleOrderClick = (product) => {
    if (!isAuthenticated) {
      toast.info("Sipariş vermek için giriş yapmalısınız");
      navigate("/giris");
      return;
    }
    setOrderDialog({ open: true, product });
    setOrderDescription("");
  };

  const handleOrderSubmit = async () => {
    if (!orderDialog.product) return;

    setOrdering(true);
    try {
      await axios.post(`${API}/orders`, {
        product_id: orderDialog.product.id,
        product_name: orderDialog.product.name,
        project_type: orderDialog.product.category,
        description: orderDescription || orderDialog.product.description,
        features: orderDialog.product.features || [],
        price: orderDialog.product.price
      });

      toast.success("Siparişiniz başarıyla oluşturuldu!");
      setOrderDialog({ open: false, product: null });
      navigate("/dashboard");
    } catch (error) {
      console.error("Error creating order:", error);
      toast.error("Sipariş oluşturulurken hata oluştu");
    } finally {
      setOrdering(false);
    }
  };

  const filteredProducts = selectedCategory === "all" 
    ? products 
    : products.filter(p => p.category === selectedCategory);

  return (
    <div className="pt-16 md:pt-20" data-testid="products-page">
      {/* Hero */}
      <section className="py-20 bg-[#0a0a0f] relative overflow-hidden">
        <div className="absolute inset-0 grid-pattern opacity-30" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center max-w-3xl mx-auto">
            <span className="text-[#00d4aa] font-mono text-sm mb-4 block">
              {'// ÜRÜNLERİMİZ'}
            </span>
            <h1 className="text-4xl sm:text-5xl font-bold text-[#f0f0f5] mb-6">
              Hazır{" "}
              <span className="gradient-text">Yazılım Çözümleri</span>
            </h1>
            <p className="text-lg text-[#9ca3af] leading-relaxed">
              İşinizi hızla dijitalleştirin. Hazır paketlerimiz ile 
              hemen başlayın veya size özel çözümler için iletişime geçin.
            </p>
          </div>
        </div>
      </section>

      {/* Category Filter */}
      <section className="py-8 bg-[#12121a] border-b border-[#2a2a3a]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap justify-center gap-3">
            {categories.map((cat) => (
              <button
                key={cat.value}
                onClick={() => setSelectedCategory(cat.value)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all flex items-center gap-2 ${
                  selectedCategory === cat.value
                    ? 'bg-[#00d4aa] text-[#0a0a0f]'
                    : 'bg-[#16161f] text-[#9ca3af] border border-[#2a2a3a] hover:border-[#00d4aa] hover:text-[#00d4aa]'
                }`}
                data-testid={`category-filter-${cat.value}`}
              >
                {cat.icon && <cat.icon className="w-4 h-4" />}
                {cat.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Products Grid */}
      <section className="py-20 bg-[#12121a]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-8 h-8 text-[#00d4aa] animate-spin" />
            </div>
          ) : filteredProducts.length === 0 ? (
            <div className="text-center py-20">
              <div className="w-20 h-20 rounded-full bg-[#16161f] border border-[#2a2a3a] flex items-center justify-center mx-auto mb-4">
                <Smartphone className="w-10 h-10 text-[#9ca3af]" />
              </div>
              <h3 className="text-xl font-semibold text-[#f0f0f5] mb-2">
                Henüz Ürün Eklenmemiş
              </h3>
              <p className="text-[#9ca3af] mb-6">
                Bu kategoride henüz ürün bulunmuyor.
              </p>
              <Link to="/iletisim" className="btn-primary inline-flex items-center gap-2">
                Özel Proje Talebi
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredProducts.map((product) => {
                const IconComponent = getCategoryIcon(product.category);
                const categoryColor = getCategoryColor(product.category);
                
                return (
                  <div
                    key={product.id}
                    className={`bg-[#16161f] border rounded-xl overflow-hidden transition-all hover:border-[#00d4aa] hover:shadow-lg hover:shadow-[#00d4aa]/10 ${
                      product.is_popular ? 'border-[#00d4aa]' : 'border-[#2a2a3a]'
                    }`}
                    data-testid={`product-card-${product.id}`}
                  >
                    {product.is_popular && (
                      <div className="bg-gradient-to-r from-[#00d4aa] to-[#0ea5e9] px-4 py-2 flex items-center justify-center gap-2">
                        <Star className="w-4 h-4 text-[#0a0a0f]" fill="#0a0a0f" />
                        <span className="text-sm font-semibold text-[#0a0a0f]">Popüler</span>
                      </div>
                    )}
                    
                    <div className="p-6">
                      <div className="flex items-start justify-between mb-4">
                        <div 
                          className="w-12 h-12 rounded-lg flex items-center justify-center"
                          style={{ 
                            background: `linear-gradient(135deg, ${categoryColor}20, ${categoryColor}10)`,
                            border: `1px solid ${categoryColor}30`
                          }}
                        >
                          <IconComponent className="w-6 h-6" style={{ color: categoryColor }} />
                        </div>
                        <span 
                          className="text-xs font-medium px-3 py-1 rounded-full"
                          style={{ 
                            backgroundColor: `${categoryColor}15`,
                            color: categoryColor
                          }}
                        >
                          {getCategoryLabel(product.category)}
                        </span>
                      </div>

                      <h3 className="text-xl font-bold text-[#f0f0f5] mb-2">
                        {product.name}
                      </h3>
                      <p className="text-[#9ca3af] text-sm mb-4 line-clamp-2">
                        {product.description}
                      </p>

                      {product.features && product.features.length > 0 && (
                        <div className="space-y-2 mb-6">
                          {product.features.slice(0, 5).map((feature, idx) => (
                            <div key={idx} className="flex items-center gap-2">
                              <CheckCircle2 className="w-4 h-4 text-[#00d4aa] flex-shrink-0" />
                              <span className="text-[#f0f0f5] text-sm">{feature}</span>
                            </div>
                          ))}
                          {product.features.length > 5 && (
                            <p className="text-[#9ca3af] text-xs pl-6">
                              +{product.features.length - 5} özellik daha
                            </p>
                          )}
                        </div>
                      )}

                      <div className="border-t border-[#2a2a3a] pt-4">
                        <div className="flex items-end justify-between mb-4">
                          <div>
                            <span className="text-2xl font-bold text-[#f0f0f5]">
                              {product.price}
                            </span>
                            {product.price_note && (
                              <p className="text-[#9ca3af] text-xs mt-1">
                                {product.price_note}
                              </p>
                            )}
                          </div>
                        </div>
                        
                        <Button
                          onClick={() => handleOrderClick(product)}
                          className="w-full bg-gradient-to-r from-[#00d4aa] to-[#0ea5e9] hover:opacity-90 text-[#0a0a0f] font-semibold py-3"
                          data-testid={`product-order-${product.id}`}
                        >
                          <ShoppingCart className="w-4 h-4 mr-2" />
                          Sipariş Ver
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* Custom Project CTA */}
      <section className="py-20 bg-[#0a0a0f] relative overflow-hidden">
        <div className="absolute inset-0 grid-pattern opacity-30" />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <span className="text-[#00d4aa] font-mono text-sm mb-4 block">
            {'// ÖZEL PROJE'}
          </span>
          <h2 className="text-3xl sm:text-4xl font-bold text-[#f0f0f5] mb-6">
            İhtiyacınıza Özel{" "}
            <span className="gradient-text">Çözüm mü Arıyorsunuz?</span>
          </h2>
          <p className="text-[#9ca3af] mb-8 max-w-2xl mx-auto">
            Hazır paketlerimiz dışında size özel bir proje mi geliştirmemizi istiyorsunuz? 
            Fikirlerinizi paylaşın, birlikte hayata geçirelim.
          </p>
          <Link
            to="/iletisim"
            className="btn-primary inline-flex items-center gap-2 text-lg px-8 py-4"
            data-testid="custom-project-cta"
          >
            <Terminal className="w-5 h-5" />
            Özel Proje Talebi
            <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </section>

      {/* Order Dialog */}
      <Dialog open={orderDialog.open} onOpenChange={(open) => setOrderDialog({ open, product: open ? orderDialog.product : null })}>
        <DialogContent className="bg-[#16161f] border-[#2a2a3a] text-[#f0f0f5] max-w-lg">
          <DialogHeader>
            <DialogTitle>Sipariş Oluştur</DialogTitle>
          </DialogHeader>

          {orderDialog.product && (
            <div className="space-y-4">
              <div className="bg-[#0a0a0f] rounded-lg p-4">
                <h3 className="font-semibold text-[#f0f0f5] mb-1">{orderDialog.product.name}</h3>
                <p className="text-[#9ca3af] text-sm mb-2">{orderDialog.product.description}</p>
                <p className="text-[#00d4aa] font-bold">{orderDialog.product.price}</p>
              </div>

              <div className="space-y-2">
                <Label>Ek Notlar (Opsiyonel)</Label>
                <Textarea
                  value={orderDescription}
                  onChange={(e) => setOrderDescription(e.target.value)}
                  placeholder="Projeniz hakkında ek detaylar veya özel istekleriniz..."
                  rows={4}
                  className="bg-[#0a0a0f] border-[#2a2a3a] resize-none"
                />
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="ghost" onClick={() => setOrderDialog({ open: false, product: null })} className="text-[#9ca3af]">
              İptal
            </Button>
            <Button 
              onClick={handleOrderSubmit} 
              disabled={ordering}
              className="bg-[#00d4aa] hover:bg-[#00b894] text-[#0a0a0f]"
            >
              {ordering ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Oluşturuluyor...</>
              ) : (
                <><ShoppingCart className="w-4 h-4 mr-2" />Siparişi Onayla</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProductsPage;
